import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { THEME_STORAGE_KEY } from '@/config/constants.js';

const ThemeContext = createContext(null);

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function resolveSystemTheme() {
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
}

function applyThemeClass(resolved) {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export function ThemeProvider({ children, defaultTheme = 'dark' }) {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored ?? defaultTheme;
  });

  const resolvedTheme = theme === 'system' ? resolveSystemTheme() : theme;

  // useLayoutEffect so the .dark toggle lands before the browser paints
  // the tree — avoids a one-frame flash of the wrong theme when the
  // marketing layout unmounts into the admin shell.
  useLayoutEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (theme !== 'system') return undefined;
    const media = window.matchMedia(MEDIA_QUERY);
    const listener = () => applyThemeClass(resolveSystemTheme());
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme(next) {
        setThemeState(next);
        localStorage.setItem(THEME_STORAGE_KEY, next);
      },
    }),
    [theme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
